import { Link } from 'react-router-dom';
import { Button } from '@accure/ui';
import { format } from 'date-fns';
import { Check, Unlink } from 'lucide-react';

import { Card } from '@/components/ui/card';
import type { Task } from '@/project-management/entities/task';
import { TaskStatus } from '@/project-management/entities/task-status';
import type { Workstream } from '@/project-management/entities/workstream';
import { useDetachTaskModal } from '@/project-management/modals/detach-task';
import { TaskQuery } from '@/project-management/queries/task.query';
import { getTaskLink } from '@/project-management/utils/get-task-link';
import { cls } from '@/utils/cls';

export type WorkstreamTaskProps = {
  workstream: Pick<Workstream, 'id'>;
  task: Pick<
    Task,
    'id' | 'status' | 'title' | 'description' | 'dueDate' | 'assignee'
  >;
  className?: string;
};

export function WorkstreamTask({ task, workstream }: WorkstreamTaskProps) {
  const showDetachTaskModal = useDetachTaskModal();
  const prefetchTaskQuery = TaskQuery.usePrefetch();

  return (
    <Card
      className="grid grid-cols-[min-content_1fr_min-content_min-content] items-center gap-4 border-none bg-gray-50 p-3"
      onMouseEnter={() => prefetchTaskQuery({ id: task.id })}
    >
      <div>
        <CompletedIcon completed={task.status === TaskStatus.DONE} />
      </div>
      <div>
        <Link
          className="font-medium"
          to={getTaskLink({ workstreamId: workstream.id, taskId: task.id })}
        >
          {task.title}
        </Link>
        {task.description ? (
          <div className="text-sm text-gray-600">{task.description}</div>
        ) : null}
      </div>

      <div className="text-right text-sm text-gray-500">
        <p className="whitespace-nowrap">
          Due: {task.dueDate ? format(task.dueDate, 'MMM d') : '-'}
        </p>
        <p className="whitespace-nowrap">Assignee: {task.assignee ?? '-'}</p>
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="text-gray-400 hover:text-red-500"
        onClick={() => showDetachTaskModal({ id: task.id })}
      >
        <Unlink className="size-4" />
      </Button>
    </Card>
  );
}

type CompletedIconProps = {
  completed: boolean;
};

function CompletedIcon({ completed }: CompletedIconProps) {
  return (
    <div
      className={cls(
        'flex size-6 items-center justify-center rounded-full',
        completed ? 'bg-primary-400' : 'bg-gray-200',
      )}
    >
      {completed ? <Check className="size-4 text-white" /> : null}
    </div>
  );
}
